import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  BeaconDetail,
  getInstallBeacons,
  InstallSummary,
  DISK_WARNING_PERCENT,
  DISK_CRITICAL_PERCENT,
} from '../../services/onpremApi';

interface InstallDetailDrawerProps {
  install: InstallSummary | null;
  open: boolean;
  onClose: () => void;
}

function formatReceivedAt(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString('es-PA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatChartLabel(isoString: string): string {
  const d = new Date(isoString);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${min}`;
}

const DRAWER_WIDTH = 720;

const InstallDetailDrawer: React.FC<InstallDetailDrawerProps> = ({
  install,
  open,
  onClose,
}) => {
  const [beacons, setBeacons] = useState<BeaconDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !install) {
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setBeacons([]);

    getInstallBeacons(install.install_id, 50, controller.signal)
      .then((data) => {
        setBeacons(data);
      })
      .catch((err) => {
        if (
          err?.name === 'CanceledError' ||
          err?.name === 'AbortError' ||
          err?.code === 'ERR_CANCELED'
        ) {
          return; // user closed drawer mid-request, not an error
        }
        const detail = err?.response?.status
          ? `HTTP ${err.response.status}`
          : err?.message || 'error desconocido';
        setError(`Error al cargar historial de beacons: ${detail}`);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [open, install]);

  // The chart needs data oldest-first for left-to-right time axis
  const chartData = [...beacons]
    .reverse()
    .map((b) => ({
      label: formatChartLabel(b.received_at),
      disk: b.disk_percent_used,
    }));

  const latest = beacons[0] ?? null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: DRAWER_WIDTH, p: 0, overflowX: 'hidden' } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'white',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {install?.client_id ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {install?.install_id ?? ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={install?.fleet_manager_version ?? '—'}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
          <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Cerrar">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 3, overflowY: 'auto', height: 'calc(100% - 72px)' }}>
        {/* Latest beacon summary */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Último Beacon
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Cargando...
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && latest && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Recibido
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatReceivedAt(latest.received_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Host OS
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {latest.host_os ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Disco utilizado
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {latest.disk_percent_used !== null &&
                  latest.disk_percent_used !== undefined
                    ? `${latest.disk_percent_used.toFixed(1)}%`
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Backend
                </Typography>
                <Chip
                  label={latest.backend_up ? 'OK' : 'Down'}
                  color={latest.backend_up ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Base de datos
                </Typography>
                <Chip
                  label={latest.db_up ? 'OK' : 'Down'}
                  color={latest.db_up ? 'success' : 'error'}
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Agentes
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {latest.agents_connected}/{latest.agents_configured}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Trabajos (24h)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {latest.jobs_last_24h}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Errores (24h)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: latest.errors_last_24h > 0 ? 'error.main' : 'inherit',
                  }}
                >
                  {latest.errors_last_24h}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Versión beacon
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {latest.beacon_version ?? '—'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
        {!loading && !error && !latest && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No hay beacons registrados para esta instalación.
          </Typography>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Disk usage chart */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Historial de uso de disco
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Hora local del navegador
        </Typography>
        {!loading && chartData.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={chartData}
                margin={{ top: 8, right: 16, left: 0, bottom: 32 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  angle={-30}
                  textAnchor="end"
                  height={60}
                />
                <YAxis domain={[0, 100]} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, 'Disco']} />
                {/* Warning threshold */}
                <ReferenceLine
                  y={DISK_WARNING_PERCENT}
                  stroke="#ff9800"
                  strokeDasharray="4 4"
                  label={{ value: `${DISK_WARNING_PERCENT}%`, position: 'right', fontSize: 10, fill: '#ff9800' }}
                />
                {/* Critical threshold */}
                <ReferenceLine
                  y={DISK_CRITICAL_PERCENT}
                  stroke="#f44336"
                  strokeDasharray="4 4"
                  label={{ value: `${DISK_CRITICAL_PERCENT}%`, position: 'right', fontSize: 10, fill: '#f44336' }}
                />
                <Line
                  type="monotone"
                  dataKey="disk"
                  name="Disco"
                  stroke="#1976d2"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        )}
        {!loading && chartData.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sin datos de disco disponibles.
          </Typography>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Recent beacons table */}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Beacons recientes
        </Typography>
        {!loading && beacons.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Recibido
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Disco
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Backend
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    DB
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Agentes
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Trabajos 24h
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    Errores 24h
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {beacons.slice(0, 10).map((b) => (
                  <TableRow key={b.id} hover>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {formatReceivedAt(b.received_at)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {b.disk_percent_used !== null && b.disk_percent_used !== undefined
                        ? `${b.disk_percent_used.toFixed(1)}%`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={b.backend_up ? 'OK' : 'Down'}
                        color={b.backend_up ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={b.db_up ? 'OK' : 'Down'}
                        color={b.db_up ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Box
                        component="span"
                        sx={{
                          color:
                            b.agents_connected < b.agents_configured
                              ? 'warning.main'
                              : 'inherit',
                          fontWeight:
                            b.agents_connected < b.agents_configured ? 700 : 400,
                        }}
                      >
                        {b.agents_connected}/{b.agents_configured}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {b.jobs_last_24h}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Box
                        component="span"
                        sx={{
                          color: b.errors_last_24h > 0 ? 'error.main' : 'inherit',
                          fontWeight: b.errors_last_24h > 0 ? 700 : 400,
                        }}
                      >
                        {b.errors_last_24h}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && beacons.length === 0 && !error && (
          <Typography variant="body2" color="text.secondary">
            No hay beacons registrados.
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default InstallDetailDrawer;
